import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Button,
  Divider,
  Chip,
  Stack,
  Grid2 as Grid,
} from "@mui/material";
import { Home, School, People, CalendarToday } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { CardOutline } from "components/Card";
import { Organization } from "types/organization";
import { AcademicYear } from "types/academicYear";
import { GraduationClass } from "types/graduationClass";
import { useClinicText } from "hooks/useClinicText";
import { dismissOnboarding } from "utils/onboarding";

interface CompleteStepProps {
  organization?: Organization;
  academicYear?: AcademicYear;
  graduationClass?: GraduationClass;
  studentsAdded?: string[];
}

export const CompleteStep: React.FC<CompleteStepProps> = ({
  organization,
  academicYear,
  graduationClass,
  studentsAdded,
}) => {
  const navigate = useNavigate();
  const { textYearlyCurriculum, textGraduationClass } = useClinicText(
    false,
    organization,
  );

  React.useEffect(() => {
    dismissOnboarding(organization?.org_id);
  }, [organization?.org_id]);

  const handleGoToHome = () => {
    navigate("/home");
  };

  return (
    <Box sx={{ mx: "auto", p: 3 }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          Onboarding Complete
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Your organization setup has been successfully completed!
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        {organization && (
          <Grid size={{ xs: 12, md: 4 }}>
            <CardOutline
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <School sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6">Organization</Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Name:</strong> {organization.name}
                </Typography>
                <Typography variant="body1">
                  <strong>Type:</strong> {organization.type || "Educational"}
                </Typography>
              </CardContent>
            </CardOutline>
          </Grid>
        )}

        {academicYear && (
          <Grid size={{ xs: 12, md: 4 }}>
            <CardOutline
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CalendarToday sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6">{textYearlyCurriculum}</Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Period:</strong> {academicYear.name}
                </Typography>
                {academicYear.start_date && academicYear.end_date && (
                  <Typography
                    variant="body1"
                    sx={{ mb: 1 }}
                    color="text.secondary"
                  >
                    From{" "}
                    {new Date(academicYear.start_date).toLocaleDateString()} to{" "}
                    {new Date(academicYear.end_date).toLocaleDateString()}
                  </Typography>
                )}
                {academicYear.tags && academicYear.tags.length > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      <strong>Tags:</strong>
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {academicYear.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.name}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Stack>
                )}
              </CardContent>
            </CardOutline>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 4 }}>
          <CardOutline
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <People sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Students</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Total Students:</strong> {studentsAdded?.length || 0}
              </Typography>
              {graduationClass && (
                <Typography variant="body1">
                  <strong>{textGraduationClass}:</strong> {graduationClass.name}
                </Typography>
              )}
            </CardContent>
          </CardOutline>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Typography variant="h6" gutterBottom>
          Ready to get started?
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<Home />}
          onClick={handleGoToHome}
          sx={{ minWidth: 200 }}
        >
          Go to Home
        </Button>
      </Stack>
    </Box>
  );
};
